import React from "react";
import { PropTypes } from "prop-types";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import {
    MrbButton,
    MrbFieldInput,
    MrbFieldCheckbox,
    MrbRecaptcha,
    MrbEditFormLayout,
    MrbRecaptchaPrivacyPolicy,
} from "mrb/components";
import { MrbRadioButtons } from "mrb/components/radio";
import { PasswordFieldInfo, StripeCreditCardField } from "common/components";
import { SubscriptionPrice } from "application/payment/components";
import { defaultTemplate } from "common/hoc";
import { subscriptionPlans as subscriptionPlansConstant } from "common/constants";
import { mergeCss } from "mrb/common/utils";
import { map } from "lodash";
import { observer } from "mobx-react";

function RegisterTemplate({ registerViewStore, t }) {
    const { form, success, recaptchaViewStore } = registerViewStore;
    const stripe = useStripe();
    const elements = useElements();

    if (stripe) {
        registerViewStore.setStripe(stripe);
    }

    const onSubmit = (event) => {
        if (registerViewStore.isSubscribing) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            const cardElement = elements ? elements.getElement(CardElement) : null;
            registerViewStore.submitWithSubscription(cardElement);
            return;
        }
        return form.onSubmit(event);
    };

    return (
        <React.Fragment>
            <div className="c-membership__wrapper">
                <div className="c-membership__cta">
                    <p>
                        Already a member?{" "}
                        <MrbRouterLink routeName="master.public.membership.login">Sign in now</MrbRouterLink>.
                    </p>
                </div>
                <div className="c-membership__form">
                    <h1 className="c-membership__form__title">{t("REGISTER.TITLE")}</h1>
                    <MrbEditFormLayout
                        t={t}
                        form={form}
                        onSubmit={onSubmit}
                        submitLabel="REGISTER.BUTTONS.SIGN_UP"
                        submitDisabled={success}
                        footer={<RegisterFooter registerViewStore={registerViewStore} form={form} t={t} submitDisabled={success} />}
                    >
                        <MrbFieldInput classNameExtend="c-input--outline" t={t} field={form.$("firstName")} />
                        <MrbFieldInput classNameExtend="c-input--outline" t={t} field={form.$("lastName")} />
                        <MrbFieldInput
                            classNameExtend="c-input--outline"
                            t={t}
                            field={form.$("email")}
                            autoComplete="email"
                        />
                        <MrbFieldInput
                            classNameExtend="c-input--outline"
                            t={t}
                            field={form.$("confirmEmail")}
                            autoComplete="email"
                        />
                        <MrbFieldInput
                            classNameExtend="c-input--outline"
                            t={t}
                            field={form.$("password")}
                            autoComplete="new-password"
                        />
                        <PasswordFieldInfo field={form.$("password")} />
                        <MrbFieldInput
                            classNameExtend="c-input--outline"
                            t={t}
                            field={form.$("confirmPassword")}
                            autoComplete="new-password"
                        />
                        <MrbFieldCheckbox
                            t={t}
                            field={form.$("privacyPolicy")}
                            labelRender={(props) => <PrivacyPolicyLabel {...props} />}
                        />
                        <SubscribeSection registerViewStore={registerViewStore} t={t} />
                        <RegistrationStatus registerViewStore={registerViewStore} t={t} />
                    </MrbEditFormLayout>
                    <MrbRecaptcha t={t} store={recaptchaViewStore} />
                </div>
            </div>
        </React.Fragment>
    );
}

RegisterTemplate.propTypes = {
    registerViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const SubscribeSection = observer(function SubscribeSection({ registerViewStore, t }) {
    const {
        subscriptionPlans,
        selectedPlanId,
        selectedPlan,
        firstPaymentMessage,
        stripeField,
    } = registerViewStore;

    return (
        <div className="u-mar--top--med u-mar--bottom--med">
            <div className="u-mar--bottom--med">
                <label className="u-type--sml u-display--block u-mar--bottom--sml">
                    {t("REGISTER.COUPON.CHOOSE_PLAN")}
                </label>
                <MrbRadioButtons
                    id="signupPlan"
                    classNameExtend="u-mar--bottom--med"
                    options={map(subscriptionPlans, (plan) => ({ value: plan.id, label: plan.name }))}
                    value={selectedPlanId}
                    onChange={(value) => registerViewStore.setSelectedPlan(value)}
                    t={t}
                />
                {selectedPlan && (
                    <div className="c-payment__list">
                        <PlanCard plan={selectedPlan} />
                    </div>
                )}
            </div>

            {selectedPlan && (
                <React.Fragment>
                    <CouponField registerViewStore={registerViewStore} t={t} />

                    <div className="u-mar--bottom--med">
                        <StripeCreditCardField
                            field={stripeField}
                            label="APPLICATION.PREMIUM.PAYMENT.FIELDS.CREDIT_CARD"
                            t={t}
                        />
                    </div>

                    {firstPaymentMessage && (
                        <p className="u-type--sml u-type--color--note u-mar--bottom--med">
                            {t(firstPaymentMessage.key, firstPaymentMessage.params)}
                        </p>
                    )}
                </React.Fragment>
            )}
        </div>
    );
});

const CouponField = observer(function CouponField({ registerViewStore, t }) {
    const {
        showCouponField,
        couponCode,
        appliedCoupon,
        isApplyingCoupon,
        couponErrorMessageKey,
        discountSummary,
    } = registerViewStore;

    if (appliedCoupon) {
        return (
            <div className="c-payment__coupon u-mar--bottom--med">
                <div className="u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between">
                    <div>
                        <p className="u-type--sml u-type--color--note">{t("REGISTER.COUPON.APPLIED_LABEL")}</p>
                        <p className="u-type--base u-type--wgt--bold">{appliedCoupon.promoCode}</p>
                        {discountSummary && (
                            <p className="u-type--sml">{t(discountSummary.key, discountSummary.params)}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="c-btn c-btn--sml c-btn--ghost"
                        onClick={registerViewStore.removeCoupon}
                    >
                        {t("REGISTER.COUPON.REMOVE")}
                    </button>
                </div>
            </div>
        );
    }

    if (!showCouponField) {
        return (
            <div className="u-mar--bottom--med">
                <button
                    type="button"
                    className="c-btn c-btn--base c-btn--ghost"
                    onClick={() => registerViewStore.setShowCouponField(true)}
                >
                    {t("REGISTER.COUPON.HAVE_COUPON")}
                </button>
            </div>
        );
    }

    return (
        <div className="c-payment__coupon u-mar--bottom--med">
            <label className="u-type--sml u-display--block u-mar--bottom--tny">{t("REGISTER.COUPON.LABEL")}</label>
            <div className="u-display--flex">
                <input
                    type="text"
                    className="c-input c-input--text c-input--med u-mar--right--sml"
                    placeholder={t("REGISTER.COUPON.PLACEHOLDER")}
                    value={couponCode}
                    onChange={(e) => registerViewStore.onChangeCouponCode(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            registerViewStore.applyCoupon();
                        }
                    }}
                />
                <button
                    type="button"
                    className="c-btn c-btn--med c-btn--primary"
                    disabled={isApplyingCoupon || !(couponCode && couponCode.trim())}
                    onClick={registerViewStore.applyCoupon}
                >
                    {t("REGISTER.COUPON.APPLY")}
                </button>
            </div>
            {couponErrorMessageKey && (
                <p className="u-type--sml u-type--color--warning u-mar--top--tny">{t(couponErrorMessageKey)}</p>
            )}
        </div>
    );
});

function PlanCard({ plan }) {
    let variantClassName = "c-payment__card--";
    let iconClassName = "u-icon--subscription--";

    if (plan.slug === subscriptionPlansConstant.nakiRadioFree) {
        variantClassName += "secondary";
        iconClassName += "tales";
    } else if (plan.slug === subscriptionPlansConstant.nakiNigun) {
        variantClassName += "tertiary";
        iconClassName += "nigun";
    } else {
        variantClassName += "primary";
        iconClassName += "premium-naki-logo";
    }

    return (
        <div className={mergeCss("c-payment__card c-payment__card--sml", variantClassName)}>
            <div className="c-payment__body">
                <i className={mergeCss("u-icon u-icon--subscription", iconClassName)}></i>
            </div>

            <p className="c-payment__header u-type--med">
                <span>{plan.name}</span>
                <span>{plan.price === 0 ? "Free" : <SubscriptionPrice price={plan.price} />}</span>
            </p>

            {plan.features && plan.features.length > 0 && (
                <div className="c-payment__footer c-payment__footer--base">
                    {map(plan.features, (feature, index) => (
                        <p className="c-payment__body__item u-type--sml" key={index}>
                            <i className="u-icon u-icon--base u-icon--line-arrow u-mar--right--tny"></i>
                            {feature.isStrong ? <strong>{feature.description}</strong> : feature.description}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
}

function PrivacyPolicyLabel({ id }) {
    return (
        <label htmlFor={id} className="u-mar--bottom--sml">
            By signing up, I accept the NakiRadio <a href="https://nakiradio.com/terms-of-use/">Terms and Conditions</a>{" "}
            and acknowledge the
            <a href="https://nakiradio.com/privacy-policy/"> Privacy Policy</a>.
        </label>
    );
}

const RegistrationStatus = observer(function RegistrationStatus({ registerViewStore, t }) {
    if (registerViewStore.success) {
        return (
            <div className="c-validation--success u-mar--top--sml">
                <p className="c-validation__label">{t("REGISTER.SUCCESS_MESSAGE")}</p>
            </div>
        );
    }
    return null;
});

const RegisterFooter = observer(function RegisterFooter({ registerViewStore, form, submitDisabled, t }) {
    const label = registerViewStore.isSubscribing
        ? t("REGISTER.BUTTONS.SUBSCRIBE")
        : t("REGISTER.BUTTONS.SIGN_UP");
    return (
        <div className="c-form__footer">
            {registerViewStore.submitErrorMessage && (
                <p className="u-type--sml u-type--color--warning u-mar--bottom--sml">
                    {t(registerViewStore.submitErrorMessage)}
                </p>
            )}
            <MrbButton
                type="submit"
                aria-label={label}
                aria-busy={form.submitting || form.validating || registerViewStore.isProcessing}
                label={label}
                className="c-btn c-btn--primary c-btn--med u-width--100"
                disabled={
                    submitDisabled ||
                    registerViewStore.isSubscribeDisabled ||
                    registerViewStore.isProcessing ||
                    !registerViewStore.form.isValid
                }
            />
            <div className="u-separator--primary u-mar--bottom--lrg u-mar--top--lrg"></div>

            <MrbRecaptchaPrivacyPolicy className="u-type--sml u-type--center" />
        </div>
    );
});

export default defaultTemplate(RegisterTemplate);
